// =============================================================================
// useStaticSites — lista wszystkich migracji (strona /strony). Realtime na
// tabeli static_sites; nazwa strony/mapy laczona po stronie klienta.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useWebsites } from "@/hooks/useWebsites";
import { useThematicMaps } from "@/hooks/useThematicMaps";
import type { StaticSiteRow } from "@/services/staticSite/types";
import { listStaticSites } from "@/services/staticSite/migrationRunner";

export interface StaticSiteListItem {
  site: StaticSiteRow;
  websiteName: string | null;
  websiteUrl: string | null;
  mapName: string | null;
}

export function useStaticSites() {
  const [sites, setSites] = useState<StaticSiteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { websites } = useWebsites();
  const { maps } = useThematicMaps();

  const refresh = useCallback(async () => {
    try {
      setSites(await listStaticSites());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  useRealtimeSubscription({
    channel: "static_sites_list",
    table: "static_sites",
    enabled: true,
    onChange: () => { void refresh(); },
  });

  const items: StaticSiteListItem[] = useMemo(
    () => sites.map((site) => {
      const w = websites.find((x) => x.id === site.website_id);
      const m = maps.find((x) => x.id === site.map_id);
      return { site, websiteName: w?.name || null, websiteUrl: w?.url || null, mapName: m?.name || null };
    }),
    [sites, websites, maps],
  );

  return { items, loading, error, refresh };
}
